import { tokens } from "../../../../theme";
import { useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Typography,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { refreshDrawerStyles } from "./styles";
import { countRows, filterSuggestions, plural } from "./helpers";
import ManifestGroup from "./ManifestGroup";
import TargetSelect from "./TargetSelect";
import ScopeCard from "./ScopeCard";

// Chips are committed terms; the text box is the term being typed. Enter or
// comma commits, Backspace on an empty box pops the last chip.
const ManifestFilter = ({
  groups,
  release,
  filter,
  setFilter,
  inScopeCount,
}) => {
  const theme = useTheme();
  const { refreshDrawer } = refreshDrawerStyles(tokens(theme.paletteId));

  const total = countRows(groups);
  const filtering = filter.chips.length > 0 || filter.text.trim().length > 0;
  const inputRef = useRef(null);
  const suggestions = filterSuggestions(groups, filter);

  const addChip = (term) => {
    const value = term.trim();
    if (!value) return;
    setFilter((prev) => ({
      chips: prev.chips.some((c) => c.toLowerCase() === value.toLowerCase())
        ? prev.chips
        : [...prev.chips, value],
      text: "",
    }));
  };

  const removeChip = (idx) =>
    setFilter((prev) => ({
      ...prev,
      chips: prev.chips.filter((_, i) => i !== idx),
    }));

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addChip(filter.text);
    } else if (e.key === "Backspace" && !filter.text && filter.chips.length) {
      removeChip(filter.chips.length - 1);
    }
  };

  return (
    <Box sx={refreshDrawer.card}>
      <Box sx={refreshDrawer.cardHead}>
        <Typography sx={refreshDrawer.cardTitle}>Filter</Typography>
        <Typography sx={refreshDrawer.cardSubtitle}>
          {"\u2014 images and charts"}
          {release ? (
            <>
              {" from release "}
              <Box component="span" sx={refreshDrawer.cardSubtitleMono}>
                {release}
              </Box>
            </>
          ) : null}
        </Typography>
        <Typography sx={refreshDrawer.cardCount}>
          {filtering
            ? `${inScopeCount} of ${plural(total, "entry", "entries")}`
            : "All entries"}
        </Typography>
      </Box>
      <Box sx={refreshDrawer.cardBody}>
        <Box
          sx={refreshDrawer.filterField}
          onClick={() => inputRef.current?.focus()}
        >
          {filter.chips.map((chip, idx) => (
            <Box key={chip} sx={refreshDrawer.filterChip}>
              {chip}
              <IconButton
                size="small"
                sx={refreshDrawer.filterChipRemove}
                aria-label={`Remove ${chip}`}
                onClick={(e) => {
                  e.stopPropagation();
                  removeChip(idx);
                }}
              >
                <Close />
              </IconButton>
            </Box>
          ))}
          <InputBase
            inputRef={inputRef}
            sx={refreshDrawer.filterInput}
            placeholder={
              filter.chips.length
                ? "Add another term"
                : "Type an image or chart name\u2026"
            }
            value={filter.text}
            onChange={(e) =>
              setFilter((prev) => ({ ...prev, text: e.target.value }))
            }
            onKeyDown={onKeyDown}
            inputProps={{ "aria-label": "Filter manifest" }}
          />
        </Box>
        {suggestions.length ? (
          <Box sx={refreshDrawer.suggestionRow}>
            {suggestions.map((key) => (
              <Button
                key={key}
                disableRipple
                sx={refreshDrawer.suggestionButton}
                onClick={() => addChip(key)}
              >
                {`+ ${key}`}
              </Button>
            ))}
          </Box>
        ) : null}
        {filtering ? (
          <Typography sx={refreshDrawer.cardDescription}>
            Only matching rows are listed below; overrides on hidden rows are
            kept.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

const CustomPanel = ({
  labels,
  releaseLabel,
  setReleaseLabel,
  release,
  groups,
  edits,
  extras,
  filter,
  setFilter,
  inScopeCount,
  expanded,
  setExpanded,
  scope,
  setScope,
  baseConfigBranch,
  setBaseConfigBranch,
  isLoading,
  onEdit,
  onAddExtra,
  onEditExtra,
  onRemoveExtra,
}) => {
  const theme = useTheme();
  const { refreshDrawer } = refreshDrawerStyles(tokens(theme.paletteId));
  return (
    <>
      <TargetSelect
        labels={labels}
        releaseLabel={releaseLabel}
        setReleaseLabel={setReleaseLabel}
        helper={
          release
            ? `Manifest release ${release}. Changing the label reloads the rows and drops any overrides.`
            : "The manifest below is loaded from this label; changing it reloads the rows and drops any overrides."
        }
        scope={scope}
        setScope={setScope}
        baseConfigBranch={baseConfigBranch}
        setBaseConfigBranch={setBaseConfigBranch}
      />

      <ScopeCard
        title="Scope"
        variant="chips"
        scope={scope}
        setScope={setScope}
      />

      <ManifestFilter
        groups={groups}
        release={release}
        filter={filter}
        setFilter={setFilter}
        inScopeCount={inScopeCount}
      />

      {isLoading ? (
        <Box sx={refreshDrawer.loadingBox}>
          <CircularProgress size={22} color="inherit" />
        </Box>
      ) : (
        groups.map(({ group, label, rows }) => (
          <ManifestGroup
            key={group}
            group={group}
            label={label}
            rows={rows}
            edits={edits?.[group]}
            extras={extras?.[group]}
            filter={filter}
            expanded={Boolean(expanded[group])}
            onToggle={() =>
              setExpanded((prev) => ({ ...prev, [group]: !prev[group] }))
            }
            onEdit={onEdit}
            onAddExtra={() => onAddExtra(group)}
            onEditExtra={onEditExtra}
            onRemoveExtra={onRemoveExtra}
          />
        ))
      )}
    </>
  );
};

export default CustomPanel;
